import {
  Component,
  DestroyRef,
  DOCUMENT,
  ElementRef,
  Renderer2,
  afterNextRender,
  afterRenderEffect,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';

import { GlobalBookingTravelClass } from '@fcom/core/interfaces/global-booking-travel-class.interface';
import { RootPaths } from '@fcom/core/constants';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { FinnairMultiCitySearchInterface } from '@fcom/common/interfaces/flight-search.interface';

const TIMATIC_SCRIPT_SRC = 'https://widget.timatic.iata.org/scripts/v2/iata-timatic-widget-2.0.0.js';
const TIMATIC_STYLE_HREF = 'https://widget.timatic.iata.org/scripts/v2/iata-timatic-style-2.0.0.css';
const TIMATIC_SUPPORTED_LANGUAGES = ['EN', 'DE', 'ES', 'FR', 'PT', 'TR', 'ZH', 'JA'];
const TIMATIC_DEFAULT_LANGUAGE = 'EN';
const ROUND_TRIP_FIELDS = {
  origin: '#departure-roundtrip-search-input',
  destination: '#arrival-roundtrip-search-input',
};
const ONE_WAY_FIELDS = { origin: '#departure-search-input', destination: '#arrival-search-input' };
const FLIGHT_SELECTION_PATH = 'flight-selection';
const DEPARTURE_DAYS_FROM_TODAY = 7;
const DEFAULT_STAY_DAYS = 7;

interface TimaticWindow extends Window {
  timaticWidgetLanguage?: { languageCode: string | null };
}

interface TimaticTripSelection {
  origin: string;
  destination: string;
  isRoundTrip: boolean;
}

@Component({
  selector: 'fin-travel-restrictions',
  templateUrl: './travel-restrictions.component.html',
  styleUrls: ['./travel-restrictions.component.scss'],
  standalone: false,
})
export class TravelRestrictionsComponent {
  private languageService = inject(LanguageService);
  private ref = inject(ElementRef);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);
  private mediaQueryService = inject(MediaQueryService);

  readonly widget = viewChild.required<ElementRef<HTMLElement>>('widget');

  readonly isMobile = this.mediaQueryService.isBreakpoint('mobile');

  private readonly timaticTripSelection = signal<TimaticTripSelection | null>(null);
  readonly flightsPageRedirectionUrl = computed(() => {
    const selection = this.timaticTripSelection();
    return selection ? this.createRedirectionUrl(selection) : null;
  });

  private widgetChangeScheduled = false;
  private pendingIsMobile: boolean | null = null;

  constructor() {
    afterNextRender(() => {
      this.setWidgetLanguage();
      const stylesheet = this.injectStylesheet();
      this.injectScript();
      const widgetEl = this.widget().nativeElement;
      const unlistenClick = this.renderer.listen(widgetEl, 'click', this.onResultAccordionHeaderClick, {
        capture: true,
      });
      const unlistenKeydown = this.renderer.listen(widgetEl, 'keydown', this.onResultAccordionHeaderKeydown, {
        capture: true,
      });
      this.destroyRef.onDestroy(() => {
        unlistenClick();
        unlistenKeydown();
        this.renderer.removeChild(this.document.head, stylesheet);
      });
    });

    afterRenderEffect(() => {
      this.reconcileAccordionState(this.isMobile());
    });
  }

  private onResultAccordionHeaderClick = (event: Event): void => {
    const header = (event.target as HTMLElement).closest('.p-accordion-header');
    if (!header) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    const tab = header.closest('.p-accordion-tab');
    if (!tab) {
      return;
    }
    const collapsed = !tab.classList.contains('fin-collapsed');
    this.setCollapsed(tab, collapsed);
    this.syncAriaExpanded(tab, !collapsed);
  };

  private onResultAccordionHeaderKeydown = (event: KeyboardEvent): void => {
    if (event.code === 'Enter' || event.code === 'Space') {
      this.onResultAccordionHeaderClick(event);
    }
  };

  private setCollapsed(tab: Element, collapsed: boolean): void {
    if (collapsed) {
      this.renderer.addClass(tab, 'fin-collapsed');
    } else {
      this.renderer.removeClass(tab, 'fin-collapsed');
    }
  }

  private syncAriaExpanded(tab: Element, expanded: boolean): void {
    const link = tab.querySelector('.p-accordion-header-link');
    if (link) {
      this.renderer.setAttribute(link, 'aria-expanded', String(expanded));
    }
    const content = tab.querySelector('.p-toggleable-content');
    if (!content) {
      return;
    }
    this.renderer.setAttribute(content, 'aria-hidden', String(!expanded));
    if (expanded) {
      this.renderer.removeAttribute(content, 'inert');
    } else {
      this.renderer.setAttribute(content, 'inert', '');
    }
  }

  onWidgetChange(isMobile: boolean | null): void {
    this.pendingIsMobile = isMobile;
    if (this.widgetChangeScheduled) {
      return;
    }
    this.widgetChangeScheduled = true;
    queueMicrotask(() => {
      this.widgetChangeScheduled = false;
      this.readWidgetState(this.pendingIsMobile);
    });
  }

  private readWidgetState(isMobile: boolean | null): void {
    this.timaticTripSelection.set(this.parseTripSelectionFromWidget());
    this.reconcileAccordionState(isMobile);
  }

  private reconcileAccordionState(isMobile: boolean | null): void {
    const root = this.widget().nativeElement;
    const breakpoint = isMobile ? 'mobile' : 'desktop';
    Array.from(root.querySelectorAll('.p-accordion .p-accordion-tab'))
      .filter((tab) => {
        return tab.getAttribute('data-fin-accordion-breakpoint') !== breakpoint;
      })
      .forEach((tab) => {
        this.renderer.setAttribute(tab, 'data-fin-accordion-breakpoint', breakpoint);
        const collapsed = !!isMobile && !tab.classList.contains('p-accordion-tab-active');
        this.setCollapsed(tab, collapsed);
        this.syncAriaExpanded(tab, !collapsed);
      });
  }
  private createRedirectionUrl({ origin, destination, isRoundTrip }: TimaticTripSelection): string {
    const departureDate = LocalDate.now().plusDays(DEPARTURE_DAYS_FROM_TODAY);
    const flights: FinnairMultiCitySearchInterface['flights'] = [
      { origin, destination, departureDate: departureDate.toString() },
    ];
    if (isRoundTrip) {
      flights.push({
        origin: destination,
        destination: origin,
        departureDate: departureDate.plusDays(DEFAULT_STAY_DAYS).toString(),
      });
    }
    const search: FinnairMultiCitySearchInterface = { flights, cabin: GlobalBookingTravelClass.MIXED, adults: 1 };

    return `/${this.languageService.langValue}/${RootPaths.BOOKING_ROOT}/${FLIGHT_SELECTION_PATH}?json=${encodeURIComponent(JSON.stringify(search))}`;
  }

  private parseTripSelectionFromWidget(): TimaticTripSelection | null {
    const roundTripShown = this.isFieldShown(ROUND_TRIP_FIELDS.origin);
    const oneWayShown = this.isFieldShown(ONE_WAY_FIELDS.origin);

    if (roundTripShown !== oneWayShown) {
      const visible = this.parseFieldPair(roundTripShown ? ROUND_TRIP_FIELDS : ONE_WAY_FIELDS);
      return visible ? { ...visible, isRoundTrip: roundTripShown } : null;
    }

    const roundTrip = this.parseFieldPair(ROUND_TRIP_FIELDS);
    if (roundTrip) {
      return { ...roundTrip, isRoundTrip: true };
    }
    const oneWay = this.parseFieldPair(ONE_WAY_FIELDS);
    return oneWay ? { ...oneWay, isRoundTrip: false } : null;
  }

  private parseFieldPair(fields: {
    origin: string;
    destination: string;
  }): Omit<TimaticTripSelection, 'isRoundTrip'> | null {
    const origin = this.parseLocCodeFromWidget(fields.origin);
    const destination = this.parseLocCodeFromWidget(fields.destination);
    return origin && destination ? { origin, destination } : null;
  }

  private isFieldShown(selector: string): boolean {
    const field = this.widget().nativeElement.querySelector(selector)?.closest('.tw2-field');
    return !!field && field.getClientRects().length > 0;
  }

  private parseLocCodeFromWidget(selector: string): string | null {
    const root = this.widget().nativeElement;
    for (const input of Array.from(root.querySelectorAll(selector))) {
      const field: Element | null = input.closest('.tw2-field');
      const code = field?.querySelector('abbr')?.textContent?.trim();
      if (code && /^[A-Z]{3}$/.test(code)) {
        return code;
      }
    }
    return null;
  }

  private setWidgetLanguage(): void {
    const langKey = this.languageService.langKeyValue.toUpperCase();
    const languageCode = TIMATIC_SUPPORTED_LANGUAGES.includes(langKey) ? langKey : TIMATIC_DEFAULT_LANGUAGE;
    (this.document.defaultView as TimaticWindow).timaticWidgetLanguage = { languageCode };
  }

  private injectStylesheet(): HTMLLinkElement {
    const link: HTMLLinkElement = this.renderer.createElement('link');
    this.renderer.setAttribute(link, 'rel', 'stylesheet');
    this.renderer.setAttribute(link, 'href', TIMATIC_STYLE_HREF);
    this.renderer.appendChild(this.document.head, link);
    return link;
  }

  private injectScript(): void {
    const script: HTMLScriptElement = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'type', 'text/javascript');
    this.renderer.setAttribute(script, 'src', TIMATIC_SCRIPT_SRC);
    this.renderer.appendChild(this.ref.nativeElement, script);
  }
}
